import type { PurchaseHistory } from '../modules/DataTransformSchema';
import { ProductThumb } from './ProductThumb';
import { formatOrderDate } from '../utils/orderPresentation';

// auto-fill (not auto-fit) keeps empty tracks, so a lone card holds its
// column width instead of stretching across the row.
const GRID_CLASS =
  'grid gap-[18px] [grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr))]';

type OrderGridProps = {
  orders: PurchaseHistory[];
  onSelectOrder: (order: PurchaseHistory) => void;
  /** Trailing skeleton cards appended while more orders stream in. */
  trailingSkeletonCount?: number;
};

export function OrderGrid({
  orders,
  onSelectOrder,
  trailingSkeletonCount = 0,
}: OrderGridProps) {
  return (
    <div className={GRID_CLASS} data-testid="order-grid">
      {orders.map((order, index) => (
        <OrderCard
          key={`${order.order_id ?? 'item'}_${index}`}
          order={order}
          onClick={() => onSelectOrder(order)}
        />
      ))}
      {Array.from({ length: trailingSkeletonCount }).map((_, index) => (
        <OrderGridCardSkeleton key={`trailing-skeleton-${index}`} />
      ))}
    </div>
  );
}

function OrderCard({
  order,
  onClick,
}: {
  order: PurchaseHistory;
  onClick: () => void;
}) {
  const name = order.product_name || 'Unknown product';
  const price = order.product_price ?? order.order_total ?? '';
  const date = formatOrderDate(order);

  return (
    <button
      type="button"
      data-testid="order-grid-card"
      onClick={onClick}
      className="flex cursor-pointer flex-col overflow-hidden rounded-[20px] border border-rule-light bg-surface text-left transition-shadow hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
    >
      <ProductThumb
        order={order}
        className="aspect-[16/9] w-full text-[32px]"
      />
      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <b className="line-clamp-2 text-sm font-semibold leading-[1.4] text-ink">
          {name}
        </b>
        {date && <small className="text-[11.5px] text-ink-muted">{date}</small>}
        <div className="mt-auto flex items-center justify-between gap-2 pt-2">
          {order.shipment_status && (
            <span className="rounded-full bg-primary-pale px-2.5 py-1 text-[11px] font-semibold text-primary">
              {order.shipment_status}
            </span>
          )}
          {price && (
            <div className="ml-auto font-display text-[17px] font-bold text-ink">
              {price}
            </div>
          )}
        </div>
      </div>
    </button>
  );
}

export function OrderGridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className={GRID_CLASS} data-testid="order-grid-skeleton">
      {Array.from({ length: count }).map((_, index) => (
        <OrderGridCardSkeleton key={`skeleton-${index}`} />
      ))}
    </div>
  );
}

function OrderGridCardSkeleton() {
  return (
    <div className="animate-pulse overflow-hidden rounded-[20px] border border-rule-light bg-surface">
      <div className="aspect-[16/9] w-full bg-rule" />
      <div className="flex flex-col gap-2.5 p-4">
        <div className="h-3 w-[85%] rounded bg-rule" />
        <div className="h-2.5 w-[35%] rounded bg-rule" />
        <div className="h-3.5 w-[30%] self-end rounded bg-rule" />
      </div>
    </div>
  );
}

export function OrderGridEmpty({ message }: { message: string }) {
  return (
    <div
      data-testid="order-grid-empty"
      className="rounded-[20px] border border-rule-light bg-paper px-5 py-14 text-center"
    >
      <div className="mx-auto mb-3.5 flex h-[52px] w-[52px] items-center justify-center rounded-2xl bg-primary-pale text-[22px] text-primary">
        □
      </div>
      <b className="mb-1 block font-display text-base text-ink">
        No orders found
      </b>
      <span className="text-[12.5px] text-ink-muted">{message}</span>
    </div>
  );
}
